/**
 * ProfilePage — single-page editor for the user's stored profile.
 *
 * Reached from the Dashboard's "Edit profile" button (account menu)
 * or the "Edit" button on the profile summary card. Routing: AppRouter
 * mounts this under `/profile` as a ProtectedRoute that requires a
 * non-null user (i.e. signup must be complete already).
 *
 * Why "single page" not "wizard":
 *   Editing is a different job than first-time setup. Users usually
 *   want to fix ONE thing — a typo in their address, a new allergy.
 *   Forcing them through 3 wizard steps to change one field is
 *   friction. So: scrollable page, all fields visible, save anytime.
 *
 * What stays the same as SignupPage:
 *   - The validation schema (`buildProfileSchema` from /components/profile/schema)
 *   - The field components (PersonalSection, HealthSection)
 *   - Field-level UX (autoSelect on the array fields, etc.)
 *   Bug fixes in one place fix both. That's the whole point of the
 *   Session 6 refactor.
 *
 * What's different from SignupPage:
 *   - Pre-fills from Redux `user` via `userToFormDefaults`
 *   - Phone is shown READ-ONLY at the top (with a help text explaining
 *     why) — phone is the user's login identity, changing it requires
 *     a new OTP flow we haven't built
 *   - Save button is disabled while the form is pristine (no point
 *     submitting unchanged data)
 *   - Success → Snackbar + stay on page (so users can keep editing)
 *   - Cancel → return to /dashboard
 *
 * Known tech debt (NOT addressed here, see CLAUDE.md):
 *   - No "unsaved changes" warning if user navigates away while dirty.
 *     React Router v6 makes `useBlocker` more involved than it looks;
 *     deferred to a later session.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Divider,
  Snackbar,
  IconButton,
} from '@mui/material';
import Container from '@/components/PageSheet';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import LanguageToggle from '@/components/LanguageToggle';
import PersonalSection from '@/components/profile/PersonalSection';
import HealthSection from '@/components/profile/HealthSection';
import PrivacySection from '@/components/profile/PrivacySection';
import {
  buildProfileSchema,
  userToFormDefaults,
} from '@/components/profile/schema';
import { useAuth } from '@/hooks/useAuth';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsRtl } from '@/store/uiSlice';
import { userApi } from '@/services/api';
import { setUser } from '@/store/authSlice';
import { formatPhoneDisplay } from '@/utils/phone';

export function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const isRtl = useSelector(selectIsRtl);

  const [serverError, setServerError] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: zodResolver(buildProfileSchema(t)),
    // Pre-fill from Redux user. `userToFormDefaults` handles the
    // age-number-to-string conversion the form expects.
    defaultValues: userToFormDefaults(user),
    mode: 'onSubmit',
  });

  /**
   * Save handler. Calls PATCH /user/profile via userApi, updates
   * Redux on success, shows a Snackbar, and resets the form's
   * baseline so `isDirty` goes back to false (without losing values).
   *
   * @param {object} data  Zod-validated form output
   */
  const onSubmit = async (data) => {
    setServerError(null);
    try {
      const updated = await userApi.updateProfile(data);
      dispatch(setUser(updated));
      // reset() with the updated values makes isDirty false again
      // while keeping the displayed values — equivalent to "the
      // current form state IS the new baseline".
      reset(userToFormDefaults(updated));
      setSuccessOpen(true);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleApiError = (err) => {
    const status = err?.response?.status;
    if (!err?.response) {
      setServerError(t('auth.networkError'));
      return;
    }
    if (status === 400) {
      setServerError(t('signup.errorValidation'));
      return;
    }
    setServerError(t('errors.serverError'));
  };

  const handleCancel = () => {
    // No confirmation prompt yet (see file-level tech debt note). If
    // we add unsaved-changes warning later, gate this navigation on it.
    navigate('/dashboard');
  };

  // Defensive: ProtectedRoute should keep us off this page when user
  // is null, but a transient Redux state shouldn't blow up the render.
  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography>{t('common.loading')}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header row: back button + title + language toggle */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={handleCancel}
              aria-label={t('common.back')}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <EditNoteIcon color="primary" />
            <Typography variant="h5" component="h1" fontWeight={700}>
              {t('profile.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {t('profile.subtitle')}
        </Typography>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={3}>
              {/* Read-only phone field at the top — identifies the user
                  visually but can't be edited from this form. */}
              <Box>
                <TextField
                  label={t('auth.phoneLabel')}
                  value={formatPhoneDisplay(user.phone || '')}
                  fullWidth
                  disabled
                  // Phone is bidi-neutral; force LTR display so the
                  // leading "+" / "0" reads naturally even in RTL.
                  inputProps={{ dir: 'ltr' }}
                  helperText={t('profile.phoneReadOnlyHint')}
                />
              </Box>

              <Divider textAlign={isRtl ? 'right' : 'left'}>
                <Typography variant="overline" color="text.secondary">
                  {t('signup.steps.personal')}
                </Typography>
              </Divider>

              <PersonalSection control={control} errors={errors} t={t} />

              <Divider textAlign={isRtl ? 'right' : 'left'}>
                <Typography variant="overline" color="text.secondary">
                  {t('signup.steps.health')}
                </Typography>
              </Divider>

              {/* showHint=false here; on the edit page the "optional"
                  reassurance feels redundant — the user has already
                  filled this in once. */}
              <HealthSection
                control={control}
                errors={errors}
                t={t}
                showHint={false}
              />

              {serverError && (
                <Alert
                  severity="error"
                  onClose={() => setServerError(null)}
                >
                  {serverError}
                </Alert>
              )}

              {/* Action row: Cancel (left) + Save (right) */}
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={2}
                sx={{ mt: 1 }}
              >
                <Button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  // Disable Save when nothing has changed — submitting a
                  // pristine form is a wasted round-trip.
                  disabled={!isDirty || isSubmitting}
                  startIcon={<SaveIcon />}
                >
                  {isSubmitting ? t('profile.saving') : t('profile.save')}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Paper>

        <PrivacySection />
      </Stack>

      <Snackbar
        open={successOpen}
        autoHideDuration={3500}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessOpen(false)}
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={() => setSuccessOpen(false)}
              aria-label={t('common.cancel')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {t('profile.successMessage')}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ProfilePage;
